// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import ChannelPins from '@app/features/channel/state/ChannelPins';
import ForumPosts from '@app/features/forum/state/ForumPosts';
import type {GatewayHandlerContext} from '@app/features/gateway/events/EventRouter';
import GuildMembers from '@app/features/member/state/GuildMembers';
import MessageReactions from '@app/features/messaging/state/MessageReactions';
import Messages from '@app/features/messaging/state/MessagingMessages';
import SavedMessages from '@app/features/messaging/state/SavedMessages';
import {notifyRemoteAddReaction} from '@app/features/messaging/utils/ReactionAddListeners';
import {getReactionEmojiDisplayText, type ReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';
import MentionFeed from '@app/features/notification/state/MentionFeed';
import Notification from '@app/features/ui/state/Notification';
import type {GuildMemberData} from '@fluxer/schema/src/domains/guild/GuildMemberSchemas';

interface ReactionEmojiPayload {
	id?: string | null;
	name?: string | null;
}

interface MessageReactionAddPayload {
	user_id: string;
	channel_id: string;
	message_id: string;
	emoji: ReactionEmojiPayload;
	guild_id?: string;
	member?: GuildMemberData;
	message_author_id?: string;
	mention_everyone?: boolean;
	mention_users?: Array<string>;
	mention_roles?: Array<string>;
	flags?: number;
	content?: string;
}

export function handleMessageReactionAdd(data: MessageReactionAddPayload, _context: GatewayHandlerContext): void {
	const emoji = data.emoji as ReactionEmoji;
	if (data.guild_id && data.member) {
		GuildMembers.hydrateIfMissing(data.guild_id, data.member);
	}
	SavedMessages.handleMessageReactionAdd(data.message_id);
	MessageReactions.handleReactionAdd(data.message_id, data.user_id, emoji);
	ChannelPins.handleMessageReactionAdd(data.channel_id, data.message_id);
	ForumPosts.handleReaction(
		data.channel_id,
		data.message_id,
		emoji,
		true,
		data.user_id === Authentication.currentUserId,
	);
	MentionFeed.handleMessageReactionAdd(data.message_id);
	Messages.handleReaction({
		type: 'MESSAGE_REACTION_ADD',
		channelId: data.channel_id,
		messageId: data.message_id,
		userId: data.user_id,
		emoji,
	});
	notifyRemoteAddReaction(data.channel_id, data.message_id, emoji, data.user_id);
	Notification.handleMessageReactionAdd({
		channelId: data.channel_id,
		messageId: data.message_id,
		reactorId: data.user_id,
		emoji: getReactionEmojiDisplayText(emoji),
		messageAuthorId: data.message_author_id,
		mentionEveryone: data.mention_everyone ?? false,
		mentionUserIds: data.mention_users ?? [],
		mentionRoleIds: data.mention_roles ?? [],
		flags: data.flags ?? 0,
		content: data.content,
	});
}
